import { useEffect, useState } from "react";
import { Redirect, useLocation } from "wouter";
import {
  TenantMaxTransactionAmountInputSchema,
  type PointsRule,
  type TenantMeResponse,
} from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";
import { MerchantProfileSection } from "../components/MerchantProfileSection";

/**
 * Merchant dashboard — scan-ceiling, scan-mode, and points-rule
 * settings. Reads `GET /api/v1/tenant/me` for current state and
 * writes through `PATCH /api/v1/tenant/settings`.
 *
 * Sections (top → bottom):
 *   1. Per-transaction limit (Phase 7 — fat-finger rail). Set a new
 *      ceiling or disable, with a confirm step on disable.
 *   2. Scan mode (Phase 8) — PER_TRANSACTION | VISIT_ONLY radio. Flips
 *      that change the points-bearing matrix (POINTS/HYBRID tenants
 *      switching mode) require an atomic PATCH of BOTH `scanMode`
 *      and `pointsRule` so the server's INVALID_RULE_FOR_MODE check
 *      passes; the modal collects the new rule value inline.
 *   3. Points rule (Phase 8) — only shown for POINTS/HYBRID tenants.
 *      Form fields adapt to current `scanMode`: PER_PESO rate (e.g.
 *      "1 point per ₱1") for PER_TRANSACTION, PER_VISIT amount (e.g.
 *      "10 points per visit") for VISIT_ONLY.
 */

function formatPeso(value: string | null): string {
  if (value === null) return "Disabled";
  // value is already "X.XX" — render as ₱ with thousands separators.
  const [intPart, fracPart] = value.split(".");
  const withCommas = Number(intPart).toLocaleString("en-PH");
  return `₱${withCommas}.${fracPart}`;
}

/**
 * True for tenants whose accrual model uses `points_rule` — POINTS
 * (only) and HYBRID (points + stamps). STAMPS / VISIT_TIERS ignore
 * the rule entirely so we hide the section for them.
 */
function isPointsBearing(loyaltyMode: TenantMeResponse["loyaltyMode"]): boolean {
  return loyaltyMode === "POINTS" || loyaltyMode === "HYBRID";
}

/**
 * Render a scan_mode + points_rule pair as a single human sentence
 * for the section heading state (e.g. "Visit-only · 10 points per visit").
 */
function describeRule(rule: PointsRule): string {
  if (rule.mode === "PER_PESO") {
    return `${rule.rate} point${rule.rate === 1 ? "" : "s"} per ₱1`;
  }
  if (rule.mode === "AMOUNT_PER_POINT") {
    // D99 — inverse of PER_PESO. "1 point per ₱50 spent."
    return `1 point per ₱${rule.amount.toLocaleString("en-PH")} spent`;
  }
  return `${rule.amount} point${rule.amount === 1 ? "" : "s"} per visit`;
}

// D99 — sub-mode picker for PER_TRANSACTION points rules. The
// merchant settings UI uses this both in the standalone rule
// editor and in the scan-mode flip modal so the merchant can
// pick "points per peso" vs "pesos per point" anywhere they're
// configuring a PER_TRANSACTION rule.
type PerTxRuleKind = "PER_PESO" | "AMOUNT_PER_POINT";

function ruleToPerTxKind(rule: PointsRule): PerTxRuleKind {
  return rule.mode === "AMOUNT_PER_POINT" ? "AMOUNT_PER_POINT" : "PER_PESO";
}

/**
 * Coerce a free-text pesos value (e.g. "100000", "100,000", "100000.5")
 * into the canonical 2-decimal string the API expects. Returns null if
 * the input doesn't look like a money value at all.
 */
function canonicalisePesos(input: string): string | null {
  const cleaned = input.replace(/[,\s₱]/gu, "").trim();
  if (cleaned === "") return null;
  // Accept integer or one/two-decimal forms. Anything else (e.g.
  // "1.234.56") is rejected.
  if (!/^\d+(\.\d{1,2})?$/u.test(cleaned)) return null;
  const [intPart, fracRaw] = cleaned.split(".");
  const fracPart = (fracRaw ?? "").padEnd(2, "0");
  // Strip leading zeros except for "0" itself, so "0500" → "500".
  const intStripped = intPart.replace(/^0+(?=\d)/u, "");
  return `${intStripped}.${fracPart}`;
}

export function MerchantSettingsPage() {
  const [, navigate] = useLocation();
  const [me, setMe] = useState<TenantMeResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Phase 8 — scan_mode flip modal. When the merchant clicks the
  // "switch to <mode>" button we open this modal. For points-bearing
  // tenants the modal also collects the matching points_rule value so
  // the PATCH atomically updates both fields (the server rejects
  // mismatched intermediate states with INVALID_RULE_FOR_MODE).
  const [scanModeFlipTo, setScanModeFlipTo] = useState<
    null | "PER_TRANSACTION" | "VISIT_ONLY"
  >(null);
  const [scanModeFlipRuleValue, setScanModeFlipRuleValue] = useState("");
  const [scanModeFlipErr, setScanModeFlipErr] = useState<string | null>(null);
  // D99 — sub-mode for the PER_TRANSACTION rule when flipping into
  // it. Ignored for VISIT_ONLY targets.
  const [scanModeFlipRuleKind, setScanModeFlipRuleKind] =
    useState<PerTxRuleKind>("PER_PESO");

  // Phase 8 — standalone points_rule editor (no mode change). Used
  // when the merchant just wants to tweak the rate/amount for the
  // CURRENT scan_mode.
  const [editingRule, setEditingRule] = useState(false);
  const [ruleDraft, setRuleDraft] = useState("");
  const [ruleErr, setRuleErr] = useState<string | null>(null);
  // D99 — sub-mode for the PER_TRANSACTION standalone editor.
  // Initialised from the current rule on edit-open.
  const [ruleDraftKind, setRuleDraftKind] =
    useState<PerTxRuleKind>("PER_PESO");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await merchantFetch<TenantMeResponse>("/api/v1/tenant/me");
      if (cancelled) return;
      if (!r.ok) {
        if (r.status === 401) {
          // merchantFetch already cleared the token.
          navigate("/merchant/login");
          return;
        }
        setLoadErr(r.message);
        return;
      }
      setMe(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (!getMerchantToken()) {
    return <Redirect to="/merchant/login" />;
  }

  async function applyUpdate(value: string | null) {
    setSaving(true);
    setDraftErr(null);
    try {
      const r = await merchantFetch<TenantMeResponse>(
        "/api/v1/tenant/settings",
        {
          method: "PATCH",
          body: JSON.stringify({ maxTransactionAmount: value }),
        },
      );
      if (!r.ok) {
        if (r.status === 401) {
          navigate("/merchant/login");
          return;
        }
        setDraftErr(r.message);
        return;
      }
      setMe(r.data);
      setEditing(false);
      setConfirmingDisable(false);
      setDraft("");
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  /**
   * Atomic PATCH driver for the Phase 8 scan-mode + points-rule
   * settings. Accepts any subset of {scanMode, pointsRule} so the same
   * function backs the flip modal (both fields) and the rule-only
   * editor (single field).
   */
  async function applyScanSettingsUpdate(
    body: { scanMode?: "PER_TRANSACTION" | "VISIT_ONLY"; pointsRule?: PointsRule },
    onErr: (msg: string) => void,
    onSuccess: () => void,
  ) {
    setSaving(true);
    try {
      const r = await merchantFetch<TenantMeResponse>(
        "/api/v1/tenant/settings",
        { method: "PATCH", body: JSON.stringify(body) },
      );
      if (!r.ok) {
        if (r.status === 401) {
          navigate("/merchant/login");
          return;
        }
        onErr(r.message);
        return;
      }
      setMe(r.data);
      setSavedAt(Date.now());
      onSuccess();
    } finally {
      setSaving(false);
    }
  }

  /**
   * Submit handler for the scan-mode flip modal. For points-bearing
   * tenants we send BOTH fields so the server's matrix check passes;
   * for STAMPS/VISIT_TIERS we send only `scanMode` (the rule is
   * irrelevant for them).
   */
  async function onConfirmScanModeFlip() {
    if (!me || scanModeFlipTo === null) return;
    setScanModeFlipErr(null);
    const body: {
      scanMode: "PER_TRANSACTION" | "VISIT_ONLY";
      pointsRule?: PointsRule;
    } = { scanMode: scanModeFlipTo };

    if (isPointsBearing(me.loyaltyMode)) {
      const numeric = Number(scanModeFlipRuleValue.trim());
      if (!Number.isFinite(numeric) || numeric <= 0) {
        setScanModeFlipErr(
          scanModeFlipTo === "VISIT_ONLY"
            ? "Enter a points-per-visit amount greater than 0."
            : scanModeFlipRuleKind === "AMOUNT_PER_POINT"
              ? "Enter a peso amount per point greater than 0."
              : "Enter a points-per-peso rate greater than 0.",
        );
        return;
      }
      // D99 — VISIT_ONLY → PER_VISIT; PER_TRANSACTION uses the
      // sub-mode (PER_PESO rate or AMOUNT_PER_POINT integer pesos).
      // AMOUNT_PER_POINT must be an integer; we round here to keep
      // the API call clean (Zod would reject a fractional value).
      if (scanModeFlipTo === "VISIT_ONLY") {
        body.pointsRule = { mode: "PER_VISIT", amount: numeric };
      } else if (scanModeFlipRuleKind === "AMOUNT_PER_POINT") {
        body.pointsRule = {
          mode: "AMOUNT_PER_POINT",
          amount: Math.round(numeric),
        };
      } else {
        body.pointsRule = { mode: "PER_PESO", rate: numeric };
      }
    }

    await applyScanSettingsUpdate(
      body,
      setScanModeFlipErr,
      () => {
        setScanModeFlipTo(null);
        setScanModeFlipRuleValue("");
      },
    );
  }

  /**
   * Submit handler for the standalone points-rule editor. Mode stays
   * the same — only the rate (PER_PESO) or amount (PER_VISIT) changes.
   */
  async function onSaveRule(e: React.FormEvent) {
    e.preventDefault();
    if (!me) return;
    setRuleErr(null);
    const numeric = Number(ruleDraft.trim());
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setRuleErr("Enter a value greater than 0.");
      return;
    }
    // D99 — branch on scan_mode + sub-mode kind. AMOUNT_PER_POINT
    // is an integer (pesos per point); PER_PESO accepts decimals
    // (e.g. 1.5 points per ₱1).
    let newRule: PointsRule;
    if (me.scanMode === "VISIT_ONLY") {
      newRule = { mode: "PER_VISIT", amount: numeric };
    } else if (ruleDraftKind === "AMOUNT_PER_POINT") {
      newRule = { mode: "AMOUNT_PER_POINT", amount: Math.round(numeric) };
    } else {
      newRule = { mode: "PER_PESO", rate: numeric };
    }
    await applyScanSettingsUpdate(
      { pointsRule: newRule },
      setRuleErr,
      () => {
        setEditingRule(false);
        setRuleDraft("");
      },
    );
  }

  async function onSaveLimit(e: React.FormEvent) {
    e.preventDefault();
    const canonical = canonicalisePesos(draft);
    if (!canonical) {
      setDraftErr("Enter a peso amount like 100000 or 100000.50.");
      return;
    }
    // Mirror the server-side range check on the client so we can show a
    // friendly message before the round-trip.
    const parsed = TenantMaxTransactionAmountInputSchema.safeParse(canonical);
    if (!parsed.success) {
      setDraftErr(
        "Amount must be greater than ₱0.00 and at most ₱9,999,999,999.99.",
      );
      return;
    }
    await applyUpdate(canonical);
  }

  return (
    <div className="min-h-screen pb-12">
      <div className="mx-auto max-w-md px-6 pt-8">
        <MerchantNav current="settings" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Scan settings</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Limit how much a single cashier scan can record. Helps catch
          fat-finger typos before they hit the ledger.
        </p>

        {loadErr && (
          <p className="mt-4 rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
            {loadErr}
          </p>
        )}

        <MerchantProfileSection />

        {me && (
          <section className="card mt-6">
            <p className="text-xs uppercase tracking-wider text-ink-dark">
              Per-transaction limit
            </p>
            <p className="mt-1 text-3xl font-bold text-navy">
              {formatPeso(me.maxTransactionAmount)}
            </p>

            {savedAt && (
              <p className="mt-2 text-xs text-green-700">Saved.</p>
            )}

            {!editing && !confirmingDisable && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(true);
                    setDraft(me.maxTransactionAmount ?? "");
                    setDraftErr(null);
                    setSavedAt(null);
                  }}
                  className="btn-primary"
                >
                  {me.maxTransactionAmount === null ? "Set a limit" : "Change limit"}
                </button>
                {me.maxTransactionAmount !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmingDisable(true);
                      setSavedAt(null);
                    }}
                    className="btn-secondary"
                  >
                    Disable rail
                  </button>
                )}
              </div>
            )}

            {editing && (
              <form onSubmit={onSaveLimit} className="mt-4 space-y-3">
                <div>
                  <label htmlFor="limit" className="label">
                    New limit (PHP)
                  </label>
                  <input
                    id="limit"
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="100000.00"
                    className="input"
                  />
                </div>
                {draftErr && (
                  <p className="text-sm text-red-700">{draftErr}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setDraft("");
                      setDraftErr(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {confirmingDisable && (
              <div
                role="alertdialog"
                aria-labelledby="disable-title"
                className="mt-4 rounded-card border border-red-200 bg-red-50 p-4"
              >
                <p id="disable-title" className="text-sm font-semibold text-red-900">
                  Turn off the per-transaction limit?
                </p>
                <p className="mt-1 text-sm text-red-800">
                  Cashiers will be able to record any amount. A fat-finger typo
                  (e.g. ₱1,000,000 instead of ₱1,000) won't be caught.
                </p>
                {draftErr && (
                  <p className="mt-2 text-sm text-red-700">{draftErr}</p>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void applyUpdate(null)}
                    disabled={saving}
                    className="btn rounded-card bg-red-600 text-white hover:bg-red-700"
                  >
                    {saving ? "Disabling…" : "Yes, disable"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDisable(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {me && (
          <section className="card mt-6">
            <p className="text-xs uppercase tracking-wider text-ink-dark">
              Scan mode
            </p>
            <p className="mt-1 text-2xl font-bold text-navy">
              {me.scanMode === "VISIT_ONLY" ? "Visit-only" : "Per transaction"}
            </p>
            <p className="mt-2 text-sm text-ink-dark">
              {me.scanMode === "VISIT_ONLY"
                ? "Cashiers don't have to type an amount — every scan is a visit."
                : "Cashiers enter the transaction amount; loyalty accrues on the peso value."}
            </p>

            {!scanModeFlipTo && (
              <div className="mt-4 flex flex-wrap gap-2">
                {me.scanMode !== "PER_TRANSACTION" && (
                  <button
                    type="button"
                    onClick={() => {
                      setScanModeFlipTo("PER_TRANSACTION");
                      setScanModeFlipRuleValue("1");
                      setScanModeFlipErr(null);
                      setSavedAt(null);
                    }}
                    className="btn-primary"
                  >
                    Switch to per transaction
                  </button>
                )}
                {me.scanMode !== "VISIT_ONLY" && (
                  <button
                    type="button"
                    onClick={() => {
                      setScanModeFlipTo("VISIT_ONLY");
                      setScanModeFlipRuleValue("");
                      setScanModeFlipErr(null);
                      setSavedAt(null);
                    }}
                    className="btn-primary"
                  >
                    Switch to visit-only
                  </button>
                )}
              </div>
            )}

            {scanModeFlipTo && (
              <div
                role="alertdialog"
                aria-labelledby="scan-mode-flip-title"
                className="mt-4 rounded-card border border-navy/20 bg-cream p-4"
              >
                <p
                  id="scan-mode-flip-title"
                  className="text-sm font-semibold text-navy"
                >
                  Switch to {scanModeFlipTo === "VISIT_ONLY" ? "visit-only" : "per transaction"}?
                </p>
                <p className="mt-1 text-sm text-ink-dark">
                  {scanModeFlipTo === "VISIT_ONLY"
                    ? "Every scan will count as one visit. The cashier no longer types an amount."
                    : "Cashiers will be required to enter the transaction amount on every scan."}
                </p>
                {isPointsBearing(me.loyaltyMode) && (
                  <div className="mt-3 space-y-3">
                    {/* D99 — sub-mode selector for PER_TRANSACTION */}
                    {scanModeFlipTo === "PER_TRANSACTION" && (
                      <div>
                        <span className="label">Accrual style</span>
                        <div className="mt-1 flex flex-col gap-1.5">
                          <label className="flex items-center gap-2 text-sm text-navy">
                            <input
                              type="radio"
                              name="flip-rule-kind"
                              value="PER_PESO"
                              checked={scanModeFlipRuleKind === "PER_PESO"}
                              onChange={() => {
                                setScanModeFlipRuleKind("PER_PESO");
                                setScanModeFlipRuleValue("1");
                              }}
                            />
                            <span>Points per ₱1 spent</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-navy">
                            <input
                              type="radio"
                              name="flip-rule-kind"
                              value="AMOUNT_PER_POINT"
                              checked={
                                scanModeFlipRuleKind === "AMOUNT_PER_POINT"
                              }
                              onChange={() => {
                                setScanModeFlipRuleKind("AMOUNT_PER_POINT");
                                setScanModeFlipRuleValue("50");
                              }}
                            />
                            <span>₱ per 1 point earned</span>
                          </label>
                        </div>
                      </div>
                    )}
                    <div>
                      <label htmlFor="rule-flip" className="label">
                        {scanModeFlipTo === "VISIT_ONLY"
                          ? "Points per visit"
                          : scanModeFlipRuleKind === "AMOUNT_PER_POINT"
                            ? "₱ per 1 point"
                            : "Points per ₱1"}
                      </label>
                      <input
                        id="rule-flip"
                        type="number"
                        inputMode={
                          scanModeFlipTo === "PER_TRANSACTION" &&
                          scanModeFlipRuleKind === "AMOUNT_PER_POINT"
                            ? "numeric"
                            : "decimal"
                        }
                        min="0"
                        step={
                          scanModeFlipTo === "PER_TRANSACTION" &&
                          scanModeFlipRuleKind === "AMOUNT_PER_POINT"
                            ? "1"
                            : "any"
                        }
                        autoFocus
                        value={scanModeFlipRuleValue}
                        onChange={(e) =>
                          setScanModeFlipRuleValue(e.target.value)
                        }
                        placeholder={
                          scanModeFlipTo === "VISIT_ONLY"
                            ? "10"
                            : scanModeFlipRuleKind === "AMOUNT_PER_POINT"
                              ? "50"
                              : "1"
                        }
                        className="input"
                      />
                    </div>
                  </div>
                )}
                {scanModeFlipErr && (
                  <p className="mt-2 text-sm text-red-700">{scanModeFlipErr}</p>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void onConfirmScanModeFlip()}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? "Saving…" : "Confirm switch"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setScanModeFlipTo(null);
                      setScanModeFlipRuleValue("");
                      setScanModeFlipErr(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {me && isPointsBearing(me.loyaltyMode) && (
          <section className="card mt-6">
            <p className="text-xs uppercase tracking-wider text-ink-dark">
              Points rule
            </p>
            <p className="mt-1 text-2xl font-bold text-navy">
              {describeRule(me.pointsRule)}
            </p>
            <p className="mt-2 text-sm text-ink-dark">
              {me.scanMode === "VISIT_ONLY"
                ? "Each scan credits this many points. Amount is ignored for accrual."
                : me.pointsRule.mode === "AMOUNT_PER_POINT"
                  ? "Each ₱ of this amount spent earns 1 point (rounded down)."
                  : "Each peso of the transaction earns this many points."}
            </p>

            {!editingRule && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingRule(true);
                    // D99 — seed the value AND the sub-mode kind
                    // from the current rule so opening the editor
                    // shows the merchant exactly what they had.
                    if (me.pointsRule.mode === "PER_PESO") {
                      setRuleDraft(String(me.pointsRule.rate));
                      setRuleDraftKind("PER_PESO");
                    } else if (me.pointsRule.mode === "AMOUNT_PER_POINT") {
                      setRuleDraft(String(me.pointsRule.amount));
                      setRuleDraftKind("AMOUNT_PER_POINT");
                    } else {
                      setRuleDraft(String(me.pointsRule.amount));
                      setRuleDraftKind(ruleToPerTxKind(me.pointsRule));
                    }
                    setRuleErr(null);
                    setSavedAt(null);
                  }}
                  className="btn-primary"
                >
                  Change rule
                </button>
              </div>
            )}

            {editingRule && (
              <form onSubmit={onSaveRule} className="mt-4 space-y-3">
                {/* D99 — sub-mode selector. Only rendered for
                    PER_TRANSACTION; VISIT_ONLY keeps the old
                    single-input layout (PER_VISIT amount). */}
                {me.scanMode === "PER_TRANSACTION" && (
                  <div>
                    <span className="label">Accrual style</span>
                    <div className="mt-1 flex flex-col gap-1.5">
                      <label className="flex items-center gap-2 text-sm text-navy">
                        <input
                          type="radio"
                          name="rule-kind"
                          value="PER_PESO"
                          checked={ruleDraftKind === "PER_PESO"}
                          onChange={() => {
                            setRuleDraftKind("PER_PESO");
                            // Reset the value so a stale "50" from
                            // AMOUNT_PER_POINT doesn't accidentally
                            // become "50 points per ₱1".
                            setRuleDraft("1");
                          }}
                        />
                        <span>Points per ₱1 spent</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-navy">
                        <input
                          type="radio"
                          name="rule-kind"
                          value="AMOUNT_PER_POINT"
                          checked={ruleDraftKind === "AMOUNT_PER_POINT"}
                          onChange={() => {
                            setRuleDraftKind("AMOUNT_PER_POINT");
                            setRuleDraft("50");
                          }}
                        />
                        <span>₱ per 1 point earned</span>
                      </label>
                    </div>
                  </div>
                )}
                <div>
                  <label htmlFor="rule" className="label">
                    {me.scanMode === "VISIT_ONLY"
                      ? "Points per visit"
                      : ruleDraftKind === "AMOUNT_PER_POINT"
                        ? "₱ per 1 point"
                        : "Points per ₱1"}
                  </label>
                  <input
                    id="rule"
                    type="number"
                    inputMode={
                      me.scanMode === "PER_TRANSACTION" &&
                      ruleDraftKind === "AMOUNT_PER_POINT"
                        ? "numeric"
                        : "decimal"
                    }
                    min="0"
                    step={
                      me.scanMode === "PER_TRANSACTION" &&
                      ruleDraftKind === "AMOUNT_PER_POINT"
                        ? "1"
                        : "any"
                    }
                    autoFocus
                    value={ruleDraft}
                    onChange={(e) => setRuleDraft(e.target.value)}
                    placeholder={
                      me.scanMode === "VISIT_ONLY"
                        ? "10"
                        : ruleDraftKind === "AMOUNT_PER_POINT"
                          ? "50"
                          : "1"
                    }
                    className="input"
                  />
                  {me.scanMode === "PER_TRANSACTION" &&
                    ruleDraftKind === "AMOUNT_PER_POINT" && (
                      <p className="mt-1 text-xs text-ink-dark">
                        e.g. 50 means a customer earns 1 point for every ₱50 spent. Whole pesos only (1–10,000).
                      </p>
                    )}
                </div>
                {ruleErr && <p className="text-sm text-red-700">{ruleErr}</p>}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRule(false);
                      setRuleDraft("");
                      setRuleErr(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
